import React, { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiMoreHorizontal, FiEdit3, FiCopy, FiEye, FiShare2, FiTrash2, FiLock } = FiIcons

const ProjectCard = ({ project, onEdit, onDuplicate, onPreview, onPublish, onDelete }) => {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const formatDate = (date) => {
    if (!date) return 'Unknown'
    
    try {
      const parsedDate = new Date(date)
      if (isNaN(parsedDate.getTime())) {
        return 'Unknown'
      }
      return formatDistanceToNow(parsedDate, { addSuffix: true })
    } catch (error) {
      console.warn('Invalid date format:', date)
      return 'Unknown'
    }
  }

  const handleCardClick = (e) => {
    // Don't navigate if clicking on the menu or its buttons
    if (e.target.closest('.actions-menu')) {
      return
    }
    navigate(`/editor/${project.id}`)
  }

  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer relative"
      onClick={handleCardClick}
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-gradient-to-br from-primary-50 to-primary-100 relative">
        {(project.thumbnail_url || project.computedThumbnail) ? (
          <img
            src={project.thumbnail_url || project.computedThumbnail}
            alt={project.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback if image fails to load
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'flex'
            }}
          />
        ) : null}
        <div 
          className={`flex items-center justify-center h-full ${
            (project.thumbnail_url || project.computedThumbnail) ? 'hidden' : 'flex'
          }`}
        >
          <SafeIcon icon={FiEdit3} className="w-12 h-12 text-primary-300" />
        </div>

        {/* Status Badge */}
        <div className="absolute top-3 left-3">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              project.status === 'published'
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}
          >
            {project.status === 'published' ? 'Published' : 'Draft'}
          </span>
        </div>

        {/* Privacy Indicator */}
        {project.is_private && (
          <div className="absolute top-3 right-3">
            <SafeIcon icon={FiLock} className="w-4 h-4 text-gray-600 bg-white rounded p-1" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-gray-900 truncate flex-1 cursor-pointer hover:text-primary-600">
            {project.title}
          </h3>

          {/* Action Buttons */}
          <div className="flex items-center space-x-1 actions-menu ml-2">
            {/* Edit Button */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEdit(project.id)
              }}
              className="p-1.5 rounded-md hover:bg-blue-50 hover:text-blue-600 text-gray-500 transition-colors duration-200"
              title="Edit Project"
            >
              <SafeIcon icon={FiEdit3} className="w-4 h-4" />
            </button>

            {/* Duplicate Button */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDuplicate(project.id)
              }}
              className="p-1.5 rounded-md hover:bg-green-50 hover:text-green-600 text-gray-500 transition-colors duration-200"
              title="Duplicate Project"
            >
              <SafeIcon icon={FiCopy} className="w-4 h-4" />
            </button>

            {/* Delete Button */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowDeleteModal(true)
              }}
              className="p-1.5 rounded-md hover:bg-red-50 hover:text-red-600 text-gray-500 transition-colors duration-200"
              title="Delete Project"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {project.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
              >
                {tag}
              </span>
            ))}
            {project.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                +{project.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Timestamps */}
        <div className="text-xs text-gray-500 space-y-1">
          <div className="flex items-center space-x-1">
            <span className="text-gray-400">📅</span>
            <span>Created {formatDate(project.created_at)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="text-gray-400">✏️</span>
            <span>Edited {formatDate(project.updated_at)}</span>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-center justify-center p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            {/* Modal */}
            <div 
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiTrash2} className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Delete Project</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete <strong>"{project.title}"</strong>? 
                This will permanently remove the project and all its content.
              </p>
              
              <div className="flex space-x-3 justify-end">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onDelete(project.id)
                    setShowDeleteModal(false)
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  Delete Project
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ProjectCard